import React, { FunctionComponent } from 'react';

import { connect, useDispatch, useSelector } from 'react-redux';

import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

// Types
import User from '@common/types/user/user';
import { Category } from '@common/types/categories/category';
import { ReleaseStore } from '@common/types/release/release-store';

// Components
import Flex from '@components/layout/flex';
import NewMenu from '@components/new-menu';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';
import { CreateOrder } from '../../../store/orders/action-creators';
import { push } from 'connected-react-router';
import { getTracProPlan } from '../../../store/products/selectors';
import { TracProTableContents } from './trac-pro-table-contents';
import { useHistory, useLocation } from 'react-router';
import { CloseMenu } from '../../../store/ui/action-creators';

interface TracProMenuProps extends Partial<User> {
  className?: string;
  stores: Category<ReleaseStore>[];
  button: React.ReactNode;
  noButton?: boolean;
  isMobile: boolean;
}

const mapStateToProps = (state: AppState) => ({
  avatar: state.artist.active?.avatar,
  fullName: state.artist.active?.name,
  stores: state.categories.lists['platform'] || [],
  isMobile: state.ui.mobile,
});

const TracProMenu: FunctionComponent<TracProMenuProps> = ({
  className,
  button,
  noButton,
  avatar,
  fullName,
  stores,
  isMobile,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const history = useHistory();

  const tracPro = useSelector(getTracProPlan);

  const clientId = useSelector((state: AppState) => state.artist.active?.id);

  const section: 'release' | 'merch' | 'analytics' = location.pathname.includes(
    'merch'
  )
    ? 'merch'
    : location.pathname.includes('analytics')
    ? 'analytics'
    : 'release';

  const id = useSelector(
    (state: AppState) =>
      state.subscriptionManager.summary?.artists
        .find((artist) => artist.id === clientId)
        ?.subscriptions.find((subscription) => subscription.type === 'trac-pro')
        ?.id
  );

  // redirects
  const onUpgrade = () => {
    if (tracPro) {
      dispatch(
        new CreateOrder({
          product: tracPro,
          price: tracPro.prices.find((price) => price.frequency === 'year')!,
          quantity: 1,
        })
      );
    }
    dispatch(
      push(`/payment/${clientId}/${id || ''}?redirectTo=${location.pathname}`)
    );
  };

  const onPayAsYouGo = () => {
    if (section !== 'release') {
      return;
    }
    localStorage.setItem('Trac.PayAsYouGo', 'true');
    dispatch(new CloseMenu(undefined, true));
  };

  const goBack = () => {
    if (history.length > 2) {
      history.goBack();
    }
    dispatch(push('/dashboard'));
  };

  return (
    <StyledMenu
      className={className}
      button={noButton ? undefined : button}
      noButton={noButton}
      menuId="DISTRIBUTION_TRIAL"
      topHeader={isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      header={!isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      onClose={goBack}
      footer={
        <ButtonWrapper>
          <Button
            className="button"
            buttonType="primary"
            onClick={onUpgrade}
            fullWidth
          >
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.BTN.GO_PRO" />
          </Button>
          {section === 'release' ? (
            <Typography className="button" type="link" onClick={onPayAsYouGo}>
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.BTN.PAY_AS_YOU_GO" />
            </Typography>
          ) : null}
        </ButtonWrapper>
      }
    >
      {!isMobile && <Image src="./static/distribution.png" />}
      <Typography
        className="mb-40 title-container"
        type="analytics-nav"
        customFont="semi-bold"
        align={isMobile ? 'left' : 'center'}
        hasHover
      >
        <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TITLE" />
      </Typography>
      <TracProTableContents storesCount={stores.length} proPlan={tracPro} />
    </StyledMenu>
  );
};

export default connect(mapStateToProps)(TracProMenu);

const Image = styled.img`
  width: 100%;
  max-width: 248px;
  display: block;
  margin: 0 auto 32px;
`;

const ButtonWrapper = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-direction: column;

  .button {
    display: block;
    flex-grow: 1;
  }
`;

const StyledMenu = styled(NewMenu)`
  .title-container {
    margin: 0 auto 40px;
    max-width: 380px;
  }
  .new-menu-items-container {
    @media ${({ theme }) => theme.device.tablet} {
      .menu-header {
        min-height: 0 !important;
      }

      .menu-content {
        height: calc(100vh - 80px - 140px);
      }
    }
  }
`;
